/**
 * @file: useCreateReservationForm.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * The walk-in (lead-less) create-reservation form: the staff enter the plate +
 * customer + intake fields directly. The plate is typed or entered via the
 * shared license-plate keyboard, then the vehicle is looked up from existing
 * records to auto-fill its details; the vehicle can also be picked by brand →
 * model. The booked items are picked from the service-subject catalog. Submits
 * a reservation with no `leadId`.
 */

import { AppNavParams } from '@euler/app/Routes';
import { useCurrentUser } from '@euler/app/flows/auth/context';
import { ReservationsNavParams } from '@euler/app/flows/reservations';
import { useAppLoading } from '@euler/components/loading/Provider';
import { OrgUserRoleHelper } from '@euler/functions/OrgUserRoleHelper';
import { getCustomerTypes } from '@euler/functions/getCustomerTypes';
import { projectMileageAtDate } from '@euler/functions/projectMileageAtDate';
import { valueAfterAutoFill } from '@euler/functions/valueAfterAutoFill';
import { VehicleInfoCore } from '@euler/model/VehicleInfoCore';
import { OrgMember } from '@euler/model/entity';
import { CustomerType, OrgUserRoleType } from '@euler/model/enum';
import { ReservationItem } from '@euler/model/reservation/ReservationItem';
import { VehicleModelInfo } from '@euler/model/vehicle';
import { useServiceFactory } from '@euler/services/factory';
import { getVehicleAgeByVin } from '@euler/utils/vin/getVehicleAgeByVin';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Toast from 'react-native-root-toast';

// A vehicle descriptor entered through the brand → model picker (no VIN / fuel /
// year detail — those only arrive from an existing-record lookup).
type PickedVehicle = {
  vehicleName: string;
  vehicleBrandName: string;
  vehicleModel: string;
  vehicleManufacturer?: string;
};

type ServiceAgentRef = { id: number; name: string };

// What the last plate lookup wrote into the form, so the next one can replace
// its own values without overwriting anything the staff typed.
type AutoFilledFields = {
  customerName: string;
  customerPhone: string;
  customerType: CustomerType | undefined;
};

export type CreateReservationFormState = ReturnType<
  typeof useCreateReservationForm
>;

export const useCreateReservationForm = () => {
  const { reservationService, vehicleInfoService } = useServiceFactory();
  const loading = useAppLoading();
  const navigation =
    useNavigation<StackNavigationProp<AppNavParams & ReservationsNavParams>>();
  const currentUser = useCurrentUser();

  const [licensePlateNo, setLicensePlateNo] = useState('');
  const [vehicle, setVehicle] = useState<VehicleInfoCore | undefined>(
    undefined,
  );
  const [pickedVehicle, setPickedVehicle] = useState<PickedVehicle | undefined>(
    undefined,
  );
  const [lookingUp, setLookingUp] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [arrival, setArrival] = useState<Date | undefined>(undefined);
  const [tags, setTags] = useState<string[]>([]);
  const [mileage, setMileage] = useState('');
  // The staff booking the reservation are usually its service agent, so they
  // start out selected when they hold that role (as the create-order form
  // does); anyone else picks one.
  const [serviceAgent, setServiceAgent] = useState<ServiceAgentRef | undefined>(
    () => {
      const user = currentUser?.user;
      if (!user?.memberId) return undefined;
      const roleHelper = new OrgUserRoleHelper(user.role);
      return roleHelper.hasRole(OrgUserRoleType.ServiceAgents)
        ? { id: user.memberId, name: user.name }
        : undefined;
    },
  );
  const [customerType, setCustomerType] = useState<CustomerType | undefined>(
    undefined,
  );
  const [remark, setRemark] = useState('');
  const [items, setItems] = useState<ReservationItem[]>([]);

  // Look the vehicle up from existing records so its details and the customer
  // who last brought it in auto-fill; a hit clears any earlier manual pick. A
  // miss simply clears the resolved vehicle (a brand-new walk-in books with the
  // plate alone). A request-id guards against a slower earlier lookup landing
  // after a newer one (stale vehicle for a changed plate).
  const lookupSeqRef = useRef(0);
  const autoFilledRef = useRef<AutoFilledFields | undefined>(undefined);
  const lookupVehicle = useCallback(
    async (plateArg: string) => {
      const plate = plateArg.trim();
      if (!plate) {
        setVehicle(undefined);
        return;
      }
      const seq = ++lookupSeqRef.current;
      setLookingUp(true);
      try {
        const found = await vehicleInfoService.getExistingVehicleInfo({
          licensePlateNo: plate,
        });
        if (seq !== lookupSeqRef.current) return;
        setVehicle(found ?? undefined);
        if (found) {
          setPickedVehicle(undefined);
        }
        // The customer is only carried for a vehicle this org has served, so a
        // miss (or another org's vehicle) leaves the fields to be entered by
        // hand rather than keeping the previous plate's customer.
        const filled: AutoFilledFields = {
          customerName: found?.customerName ?? '',
          customerPhone: found?.customerMobile ?? '',
          customerType: found?.customerType,
        };
        const prev = autoFilledRef.current;
        setCustomerName(x =>
          valueAfterAutoFill(x, filled.customerName, prev?.customerName),
        );
        setCustomerPhone(x =>
          valueAfterAutoFill(x, filled.customerPhone, prev?.customerPhone),
        );
        setCustomerType(x =>
          valueAfterAutoFill(x, filled.customerType, prev?.customerType),
        );
        autoFilledRef.current = filled;
      } catch (e) {
        console.error(e);
      } finally {
        if (seq === lookupSeqRef.current) setLookingUp(false);
      }
    },
    [vehicleInfoService],
  );

  // The expected mileage is what the odometer should read on the arrival date,
  // projected from the vehicle's last reading at its average yearly usage — so
  // it follows the arrival date rather than repeating the last reading. It
  // re-projects as the vehicle or the date changes, until the staff enter a
  // figure of their own.
  const autoFilledMileageRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    let projected = '';
    if (arrival && vehicle?.vehicleMileage) {
      // Without an age to decode from the VIN there is no usage rate to
      // project with, and the last reading is the best estimate available.
      projected = String(
        projectMileageAtDate(
          vehicle.vehicleMileage,
          getVehicleAgeByVin(vehicle.vin),
          arrival,
        ) ?? vehicle.vehicleMileage,
      );
    }
    // Read before the assignment below: the updater runs on the next render,
    // by which time the ref already holds the new projection and every
    // re-projection would look like a value the staff typed.
    const lastAutoFilled = autoFilledMileageRef.current;
    setMileage(x => valueAfterAutoFill(x, projected, lastAutoFilled));
    autoFilledMileageRef.current = projected;
  }, [vehicle, arrival]);

  // Typed plate entry: a new plate drops any resolved/picked descriptor so a
  // stale one isn't kept (the plate keyboard applies the same reset on done).
  const onChangeLicensePlate = useCallback((next: string) => {
    setLicensePlateNo(next);
    setVehicle(undefined);
    setPickedVehicle(undefined);
  }, []);

  // Run the lookup against the currently-typed plate (the input's blur handler).
  const onLookupPlate = useCallback(() => {
    void lookupVehicle(licensePlateNo);
  }, [lookupVehicle, licensePlateNo]);

  // Enter the plate via the shared license-plate keyboard (`_plate` overlay);
  // applying a new plate resets any resolved descriptor, then re-looks up.
  const onOpenPlateKeyboard = useCallback(() => {
    navigation.navigate('_plate', {
      licensePlateNo,
      onDone: (value: string) => {
        const plate = value.trim().toUpperCase();
        setLicensePlateNo(plate);
        setVehicle(undefined);
        setPickedVehicle(undefined);
        void lookupVehicle(plate);
      },
    });
  }, [navigation, licensePlateNo, lookupVehicle]);

  // The resolved vehicle label, preferring a manual pick over a lookup. Shows
  // "brand model" when both are known, else the bundled vehicle name.
  const vehicleLabel = useMemo(() => {
    const brandName =
      pickedVehicle?.vehicleBrandName ?? vehicle?.vehicleBrandName;
    const modelName = pickedVehicle?.vehicleModel ?? vehicle?.vehicleModel;
    const name = pickedVehicle?.vehicleName ?? vehicle?.vehicleName;
    if (brandName && modelName) return `${brandName} ${modelName}`;
    return name || '';
  }, [pickedVehicle, vehicle]);

  const onPickVehicle = useCallback(() => {
    navigation.navigate('_vehicleBrandSelector', {
      selected: {
        brand: pickedVehicle?.vehicleBrandName ?? vehicle?.vehicleBrandName,
        model: pickedVehicle?.vehicleModel ?? vehicle?.vehicleModel,
      },
      onSelect: (brand: string, model: VehicleModelInfo) => {
        setPickedVehicle({
          vehicleName: `${brand} ${model.name}`,
          vehicleBrandName: brand,
          vehicleModel: model.name,
          vehicleManufacturer: model.manufacturer || undefined,
        });
        // A manual pick overrides the looked-up vehicle; it carries no VIN.
        setVehicle(undefined);
      },
    });
  }, [navigation, pickedVehicle, vehicle]);

  const addTag = useCallback((name: string) => {
    setTags(prev => (prev.includes(name) ? prev : [...prev, name]));
  }, []);
  const removeTag = useCallback((name: string) => {
    setTags(prev => prev.filter(x => x !== name));
  }, []);

  const toggleCustomerType = useCallback((type: CustomerType) => {
    setCustomerType(prev => (prev === type ? undefined : type));
  }, []);

  const onPickAgent = useCallback(() => {
    navigation.push('_serviceAgentSelector', {
      selectedServiceAgentId: serviceAgent?.id,
      onSelect: (member: OrgMember) =>
        setServiceAgent({ id: member.id, name: member.name }),
    });
  }, [navigation, serviceAgent?.id]);

  const onEditItems = useCallback(() => {
    navigation.navigate('_pickReservationItems', {
      initialItems: items,
      vehicle: { licensePlateNo: licensePlateNo.trim(), vin: vehicle?.vin },
      onPicked: setItems,
    });
  }, [navigation, items, licensePlateNo, vehicle?.vin]);

  const onSubmit = useCallback(async () => {
    if (lookingUp) {
      Toast.show('车辆识别中，请稍候', { position: Toast.positions.CENTER });
      return;
    }
    const plate = licensePlateNo.trim();
    if (!plate) {
      Toast.show('请输入车牌号', { position: Toast.positions.CENTER });
      return;
    }
    if (!arrival) {
      Toast.show('请选择预计到店时间', { position: Toast.positions.CENTER });
      return;
    }
    if (!tags.length) {
      Toast.show('请选择业务类型', { position: Toast.positions.CENTER });
      return;
    }
    if (!serviceAgent) {
      Toast.show('请选择服务顾问', { position: Toast.positions.CENTER });
      return;
    }
    const mileageNum = mileage.trim() ? Number(mileage) : null;
    await loading.showWithAction(async () => {
      await reservationService.create({
        // No leadId → a walk-in booking from the entered fields.
        expectedArrivalAt: arrival.toISOString(),
        items,
        licensePlateNo: plate,
        // A manual pick carries no VIN; lookup detail only from `vehicle`.
        vin: vehicle?.vin || null,
        currentMileage: vehicle?.vehicleMileage ?? null,
        vehicleName: pickedVehicle?.vehicleName ?? vehicle?.vehicleName ?? null,
        vehicleBrandName:
          pickedVehicle?.vehicleBrandName ?? vehicle?.vehicleBrandName ?? null,
        vehicleManufacturer:
          pickedVehicle?.vehicleManufacturer ??
          vehicle?.vehicleManufacturer ??
          null,
        vehicleModel:
          pickedVehicle?.vehicleModel ?? vehicle?.vehicleModel ?? null,
        vehicleModelYear: vehicle?.vehicleModelYear || null,
        vehicleDisplacement: vehicle?.vehicleDisplacement || null,
        vehicleTrimLevel: vehicle?.vehicleTrimLevel || null,
        vehicleFuelType: vehicle?.vehicleFuelType || null,
        customerName: customerName.trim() || null,
        customerPhone: customerPhone.trim() || null,
        tags: tags.join(','),
        vehicleMileage:
          mileageNum != null && !Number.isNaN(mileageNum) ? mileageNum : null,
        serviceAgentId: serviceAgent.id,
        serviceAgentName: serviceAgent.name,
        customerType: customerType ?? null,
        remark: remark.trim() || null,
      });
      Toast.show('已创建预约', { position: Toast.positions.BOTTOM });
      navigation.goBack();
    });
  }, [
    arrival,
    customerName,
    customerPhone,
    customerType,
    items,
    licensePlateNo,
    loading,
    lookingUp,
    mileage,
    navigation,
    pickedVehicle,
    remark,
    reservationService,
    serviceAgent,
    tags,
    vehicle,
  ]);

  return {
    licensePlateNo,
    onChangeLicensePlate,
    onLookupPlate,
    onOpenPlateKeyboard,
    vehicle,
    vehicleLabel,
    lookingUp,
    onPickVehicle,
    customerName,
    setCustomerName,
    customerPhone,
    setCustomerPhone,
    arrival,
    setArrival,
    tags,
    addTag,
    removeTag,
    mileage,
    setMileage,
    serviceAgent,
    onPickAgent,
    customerTypes: getCustomerTypes(),
    customerType,
    toggleCustomerType,
    remark,
    setRemark,
    items,
    onEditItems,
    onSubmit,
  };
};
